import { useEffect, useRef, useState } from "react";
import DoorReturnMonitoringDashboard from "./DoorReturnMonitoringDashboard";
import { getAllMsgDetails, getDoorEventsByHour } from "../../services/dashboard.api";

/* ─────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────── */

// "2026-07-02 11:31:17" -> needs a "T" for reliable parsing
function toUnixSeconds(dateStr) {
  if (!dateStr) return null;
  const iso = dateStr.replace(" ", "T");
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

// getAllMsgDetails' Contact values are inconsistent ("Open", "Close",
// "CLose", mixed casing) — normalize to a clean "Open" | "Close".
function normalizeContact(raw) {
  const v = (raw || "").trim().toLowerCase();
  if (v.startsWith("open")) return "Open";
  if (v.startsWith("clos")) return "Close"; // covers "close" and the "CLose" typo
  return null;
}

// getAllMsgDetails has no room field, only deviceID. Assigns a stable
// "A-001"-style label per device so the same device always shows the
// same room across polls (matches the look of the old fake room data).
function buildDeviceRoomMap(deviceIds) {
  const sorted = [...new Set(deviceIds)].sort();
  const map = {};
  sorted.forEach((id, index) => {
    const section = String.fromCharCode(65 + Math.floor(index / 100));
    const roomNumber = String((index % 100) + 1).padStart(3, "0");
    map[id] = `${section}-${roomNumber}`;
  });
  return map;
}

/**
 * getAllMsgDetails returns a flat, noisy event log: one row per
 * Open/Close reading, often with several repeated "Open" pings in a row
 * (the sensor re-announcing, not new events) before a "Close" arrives.
 * This walks each device's timeline chronologically and collapses it
 * into open→close cycles:
 *   - the first "Open" after a "Close" (or after nothing) starts a cycle
 *   - repeated "Open" pings before the next "Close" are ignored
 *   - the next "Close" ends that cycle
 *   - a device still open at the end of the window produces one cycle
 *     with closedAt: null
 * A device that opened and closed twice inside the window becomes two
 * separate rows, not one.
 */
function mapMsgDetailsToResidents(events = [], windowHours = 24) {
  // ------------------------------------------------------------------
  // Use the latest event from the backend as the "current" time.
  // This ensures we always process the last 24 hours relative to the
  // data returned by the backend rather than the user's system clock.
  // ------------------------------------------------------------------
  const allTimestamps = events
    .map((e) => toUnixSeconds(e.time))
    .filter((ts) => ts !== null);

  if (allTimestamps.length === 0) {
    return [];
  }

  const latestEventTs = Math.max(...allTimestamps);
  const cutoff = latestEventTs - windowHours * 60 * 60;

  const byDevice = new Map();

  for (const e of events) {
    const contact = normalizeContact(e.Contact);
    const ts = toUnixSeconds(e.time);

    // Ignore invalid records and anything outside the last 24 hours
    // relative to the latest backend event.
    if (!contact || ts === null || ts < cutoff) continue;

    if (!byDevice.has(e.deviceID)) {
      byDevice.set(e.deviceID, []);
    }

    byDevice.get(e.deviceID).push({
      contact,
      ts,
    });
  }

  const roomMap = buildDeviceRoomMap([...byDevice.keys()]);
  const residents = [];

  for (const [deviceID, rawEvents] of byDevice.entries()) {
    // Sort oldest -> newest
    const timeline = [...rawEvents].sort((a, b) => a.ts - b.ts);

    let pendingOpenAt = null;

    for (const evt of timeline) {
      if (evt.contact === "Open") {
        // Only start the cycle on the FIRST Open ping after a Close
        // (or after nothing). Repeated Open pings before the next
        // Close are ignored, so we keep the earliest openedAt.
        if (pendingOpenAt === null) {
          pendingOpenAt = evt.ts;
        }
      } else if (evt.contact === "Close") {
        if (pendingOpenAt !== null) {
          residents.push({
            id: `${deviceID}-${pendingOpenAt}`,
            room: roomMap[deviceID],
            deviceId: deviceID,
            contact: "Closed",
            openedAt: pendingOpenAt,
            closedAt: evt.ts,
          });

          pendingOpenAt = null;
        }
      }
    }

    // Device is still open
    if (pendingOpenAt !== null) {
      residents.push({
        id: `${deviceID}-${pendingOpenAt}`,
        room: roomMap[deviceID],
        deviceId: deviceID,
        contact: "Open",
        openedAt: pendingOpenAt,
        closedAt: undefined,
      });
    }
  }

  // Show latest activity first
  residents.sort(
    (a, b) => (b.closedAt || b.openedAt) - (a.closedAt || a.openedAt)
  );

  return residents;
}

function mapHourlyEvents(events = []) {
  return events.map((event) => ({
    hour: new Date(event.hour).toLocaleTimeString("en-GB", {
      timeZone: "Europe/London",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
    opens: event.openEvents,
    overdue: event.overdue,
  }));
}

/* ─────────────────────────────────────────────────────────
   CONTAINER — owns data fetching, passes plain props down to
   the presentational DoorReturnMonitoringDashboard component.

   Behaviour:
   - On mount: fetch residents (last 24h, reconstructed from the raw
     event log) AND the full last-24h hourly chart, unconditionally.
   - After mount: poll residents every 15s. The hourly chart is
     ONLY refetched when a poll detects an actual change in the
     residents payload (a door really opened/closed) — not on
     any fixed timer.
───────────────────────────────────────────────────────── */
export default function DoorReturnMonitoringContainer() {
  const [residents, setResidents] = useState([]);
  const [hourlyEvents, setHourlyEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Holds the last-seen residents payload (as JSON) so we can tell
  // whether a poll actually changed anything. Kept outside state/
  // setState updaters so the comparison is reliable under Strict Mode
  // and can safely trigger a side effect (fetchHourlyEvents).
  const previousResidentsJson = useRef(null);

  // Always pulls the full last-24h window. Called once on mount, and
  // again any time a residents poll detects a real door event.
  const fetchHourlyEvents = async () => {
    try {
      const response = await getDoorEventsByHour(); // last 24h
      setHourlyEvents(mapHourlyEvents(response));
    } catch (err) {
      console.error("Hourly events API error", err);
    }
  };

  const fetchResidents = async (showLoader = false) => {
    try {
      if (showLoader) setLoading(true);
      setError(null);

      const rawEvents = await getAllMsgDetails("Door Lock sensor", "24h");
      const mappedResidents = mapMsgDetailsToResidents(rawEvents, 24);
      const mappedJson = JSON.stringify(mappedResidents);

      const isFirstLoad = previousResidentsJson.current === null;
      const changed = mappedJson !== previousResidentsJson.current;

      if (changed) {
        previousResidentsJson.current = mappedJson;
        setResidents(mappedResidents);

        // Don't double-fetch the chart on first load — the mount
        // effect below already fetches it once, unconditionally.
        if (!isFirstLoad) {
          fetchHourlyEvents();
        }
      }
    } catch (err) {
      console.error("All Msg Details API Error", err);
      setError("Unable to load door return data");
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    // Initial paint: residents (last 24h) + full last-24h chart, always.
    fetchResidents(true);
    fetchHourlyEvents();

    // After that, poll residents every 15s; the chart only refreshes
    // from inside fetchResidents when a real change is detected.
    const interval = setInterval(() => fetchResidents(false), 15000);
    return () => clearInterval(interval);
  }, []);

  return (
    <DoorReturnMonitoringDashboard
      residents={residents}
      hourlyEvents={hourlyEvents}
      returnWindowMinutes={30}
      visibleRows={5}
      actionsDisabled={true}
      loading={loading}
      error={error}
    />
  );
}
